import { createColumnHelper } from '@tanstack/react-table';
import { type IntlShape } from 'react-intl';

import { RoleCellBadge } from 'src/components/table/RoleCellBadge';
import { CellUsersTableActions } from 'src/features/userManagement/components/table/CellUsersTableActions.tsx';
import { type Membership, MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { badgeRoleOptions, statusOptions } from 'src/features/userManagement/utils/constants.ts';
import { userManagementMessages } from 'src/features/userManagement/utils/messages.ts';
import {
    CellBadge,
    CellIndex,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
} from 'src/registry/components/table';
import { type BadgeOptions } from 'src/types/ui';

const columnHelper = createColumnHelper<Membership>();

export const getUsersTableColumns = ({
    openEditMembershipModal,
    openMembershipStatusModal,
    userStatusAction,
    formatMessage,
    allowedRoles,
}: {
    openEditMembershipModal: (member: Membership) => void;
    openMembershipStatusModal: (member: Membership) => void;
    userStatusAction: 'activate' | 'deactivate';
    formatMessage: IntlShape['formatMessage'];
    allowedRoles: MEMBERSHIP_ROLES[];
}) => {
    const filteredBadgeOptions: BadgeOptions = Object.fromEntries(
        Object.entries(badgeRoleOptions).filter(([role]): boolean => allowedRoles.includes(role)),
    );
    return [
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'index',
            cell: CellIndex,
            maxSize: 45,
            meta: { title: '#' },
        }),
        columnHelper.accessor('user.name', {
            id: 'user__name',
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(userManagementMessages.name),
            },
        }),
        columnHelper.accessor('user.email', {
            id: 'user__email',
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(userManagementMessages.email),
            },
        }),
        columnHelper.accessor('role', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: RoleCellBadge,
            meta: {
                title: formatMessage(userManagementMessages.role),
                badgeOptions: filteredBadgeOptions,
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(userManagementMessages.status),
                badgeOptions: statusOptions,
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            maxSize: 60,
            cell: (props) => (
                <CellUsersTableActions
                    editMembershipCallback={openEditMembershipModal}
                    membershipStatusCallback={openMembershipStatusModal}
                    userStatusAction={userStatusAction}
                    {...props}
                />
            ),
        }),
    ];
};
